import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card } from '@lombok/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { QueryState } from '@/components/QueryState';
import { useDeletePromotion, usePromotions, useUpdatePromotion } from '@/data/hooks';
import { notifyMutationError } from '@/lib/mutationToasts';
import {
  formatDate,
  kindLabel,
  PROMOTION_STATUS_ORDER,
  promotionStatus,
  promotionStatusLabel,
  promotionStatusTone,
  promotionValueLabel,
} from './labels';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byNumber, byText, sortItems, thenBy, type SortOption } from '@/lib/sort';
import type { Promotion } from '@lombok/shared';

/** Same five statuses as the Estado badge (including scheduled and expired), live first. */
const statusRank = (p: Promotion) => PROMOTION_STATUS_ORDER.indexOf(promotionStatus(p));

const PROMO_SORT: SortOption<Promotion>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((p) => p.createdAt, 'desc') },
  { value: 'az', label: 'Nombre A–Z', compare: byText((p) => p.name) },
  { value: 'za', label: 'Nombre Z–A', compare: byText((p) => p.name, 'desc') },
  { value: 'ends', label: 'Vencen primero', compare: byDate((p) => p.endsAt) },
  { value: 'status', label: 'Estado', compare: thenBy<Promotion>(byNumber(statusRank), byText((p) => p.name)) },
];
/** Non-admins only see live promotions, so ordering by status would mean nothing. */
const PROMO_SORT_PUBLIC = PROMO_SORT.filter((o) => o.value !== 'status');

interface PromocionesListPageProps {
  /** Admin path enables "Nueva promoción" + the activar/desactivar toggle. */
  basePath?: string;
  title?: string;
  description?: string;
}

/**
 * Promociones. Admin (with `basePath`) manages all promotions — create, and toggle
 * active/inactive inline. Customers/distributors (no `basePath`) get a read-only list of
 * the promotions currently live for them (the API hides non-live ones for those roles).
 */
export function PromocionesListPage({ basePath, title = 'Promociones', description }: PromocionesListPageProps) {
  const query = usePromotions();
  const update = useUpdatePromotion();
  const remove = useDeletePromotion();
  const [actingId, setActingId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'archive' | 'delete'; id: string; name: string } | null>(null);
  // Managing promotions is admin-only on the API: finance and others read the live ones.
  const admin = useIsAdmin();
  const isAdmin = Boolean(basePath) && admin;
  const [sort, setSort] = useState('recent');
  const sortOptions = isAdmin ? PROMO_SORT : PROMO_SORT_PUBLIC;
  const items = sortItems(
    (query.data?.items ?? []).filter((p) => showArchived || !p.archived),
    sortOptions,
    sort,
  );

  const toggleActive = async (promotionId: string, nextActive: boolean) => {
    setActingId(promotionId);
    try {
      await update.mutateAsync({ id: promotionId, input: { active: nextActive } });
    } catch (e) {
      notifyMutationError(e, undefined);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={description ?? (isAdmin ? 'Gestiona promociones y rebates.' : 'Promociones vigentes para tu cuenta.')}
        actions={
          isAdmin ? (
            <Link to={`${basePath}/nueva`}>
              <Button size="sm">Nueva promoción</Button>
            </Link>
          ) : undefined
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <SortSelect options={sortOptions} value={sort} onChange={setSort} />
          {isAdmin ? (
            <label className="inline-flex select-none items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-5 w-5 rounded border-border-strong text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              Mostrar archivadas
            </label>
          ) : null}
        </div>
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={items.length === 0}
          loadingVariant="table"
          skeletonCols={isAdmin ? 5 : 4}
          emptyTitle={isAdmin ? 'No hay promociones' : 'Sin promociones vigentes'}
          emptyDescription={
            isAdmin
              ? 'Crea la primera promoción con el botón “Nueva promoción”.'
              : 'No hay promociones activas en este momento. Vuelve pronto.'
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Promoción</th>
                  <th className="px-4 py-3 font-medium">Beneficio</th>
                  <th className="px-4 py-3 font-medium">Vigencia</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  {isAdmin ? <th className="px-4 py-3 text-right font-medium">Acciones</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const status = promotionStatus(p);
                  return (
                    <tr key={p.promotionId} className="border-b border-border last:border-0">
                      <td className="px-4 py-3">
                        <p className="font-medium text-text-primary">{p.name}</p>
                        {p.description ? (
                          <p className="max-w-xs truncate text-xs text-text-muted">{p.description}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-text-primary">{promotionValueLabel(p)}</span>
                        <span className="block text-xs text-text-muted">{kindLabel(p.kind)}</span>
                      </td>
                      <td className="px-4 py-3 text-text-muted">
                        {formatDate(p.startsAt)} – {formatDate(p.endsAt)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={promotionStatusTone(status)}>{promotionStatusLabel(status)}</Badge>
                      </td>
                      {isAdmin ? (
                        <td className="px-4 py-3 text-right">
                          <RowActionsMenu
                            actions={[
                              { label: 'Editar', to: `${basePath}/${p.promotionId}` },
                              ...(p.archived
                                ? [
                                    {
                                      label: 'Restaurar',
                                      onSelect: () =>
                                        void update
                                          .mutateAsync({ id: p.promotionId, input: { archived: false } })
                                          .catch((e: unknown) => notifyMutationError(e, undefined)),
                                    },
                                  ]
                                : [
                                    {
                                      label: p.active ? 'Desactivar' : 'Activar',
                                      onSelect: () => void toggleActive(p.promotionId, !p.active),
                                      disabled: actingId === p.promotionId,
                                    },
                                    {
                                      label: 'Archivar',
                                      onSelect: () => setConfirm({ kind: 'archive', id: p.promotionId, name: p.name }),
                                    },
                                  ]),
                              {
                                label: 'Eliminar',
                                tone: 'danger' as const,
                                onSelect: () => setConfirm({ kind: 'delete', id: p.promotionId, name: p.name }),
                              },
                            ]}
                          />
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </QueryState>
      </Card>

      <ConfirmDialog
        open={confirm?.kind === 'archive'}
        title="Archivar promoción"
        confirmLabel="Archivar"
        onConfirm={() => update.mutateAsync({ id: confirm?.id ?? '', input: { archived: true } })}
        onClose={() => setConfirm(null)}
      >
        «{confirm?.name}» deja de mostrarse y de aplicarse, pero se conserva. Podrás restaurarla cuando quieras.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === 'delete'}
        title="Eliminar promoción"
        confirmLabel="Eliminar definitivamente"
        danger
        onConfirm={() => remove.mutateAsync(confirm?.id ?? '')}
        onClose={() => setConfirm(null)}
      >
        «{confirm?.name}» se elimina para siempre. No es posible mientras esté vigente: desactívala primero.
      </ConfirmDialog>
    </div>
  );
}
